import React, { useEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import Button from '../../components/button';
import Checkbox from '../../components/checkbox';
import Input from '../../components/input';
import { useAngularService } from 'helpers/get_angular_service';
import notification from 'helpers/notification';
import Label from '../../components/label';
import api from 'helpers/api';
import { useExtendState } from 'helpers/use_extend_state';
import redirect from 'helpers/redirect';
import Select from '../../components/select';
import { GymBookingDetail } from '../../types/gym';
import ErrorMessage from '../../components/error_message';
import Link from '../../components/link';
import sources from 'helpers/sources';
import useOnMount from 'helpers/use_on_mount';
import Switch from '../../components/switch';
import plural from 'helpers/plural';
import useGym from 'helpers/use_gym';
import useWaivers from 'helpers/use_waivers';
import MultipleSelect from 'react/components/multiple_select';
import Textarea from 'react/components/textarea';
import hours from 'constants/hours';
import useGymUsers from 'helpers/use_gym_users';
import range from 'helpers/range';
import FeatureNames from 'constants/feature_names';

const SmsUsageMessage = () => (
  <div className="text-danger bold margin-top-8">
    SMS usage will be billed in accordance with your{' '}
    <Link route="gym.subscription.tab" routeParams={{ tab: 'subscriptions' }}>subscription plan's per-SMS fee</Link>.
  </div>
);

const CalendarLink = () => (
  <div className="helper-text">
    This setting is for display purposes only, and applies to month / week views on both the
    <Link route="gym.calendar"> Calendar</Link> page and your website timetable.
  </div>
);

const GymProfileBookings = () => {
  const [errors, setErrors] = useState<any>({});
  const loadingAnimation = useAngularService('loadingAnimation');

  const $modal = useAngularService('$modal');
  const GTMService = useAngularService('GTMService');
  const EditEventService = useAngularService('EditEventService');
  const ReactModal = useAngularService('ReactModal');

  const FeatureAvailabilityService = useAngularService('FeatureAvailabilityService');
  const gym = useGym();
  const [bookingDetail, extendBookingDetail, setBookingDetail] = useExtendState<GymBookingDetail>({
    ...gym.gym_booking_detail,
  });

  const sequencesAvailable = FeatureAvailabilityService.isAvailable('sequences');
  const bookingsAllowedAfterUnits = EditEventService.getBookingsAllowedAfterUnits();
  const restrictCancellationUnits = EditEventService.getRestrictCancellationsUnits();
  const { waivers } = useWaivers();

  const instructors = useGymUsers({ trainers: true });

  const eventReminderLeadDaysOptions = useMemo(() => range(1, 30), []);

  useOnMount(() => {
    GTMService.trackPage('Account Profile - Bookings');

    if (gym.on_freemium) {
      redirect('gym.dashboard');
    }
  });

  const waiverAttendancePolicyOptions = useMemo(() => {
    return _.compact([
      { value: 'class_kiosk', label: 'Class kiosk' },
      { value: 'general_kiosk', label: 'General kiosk' },
      { value: 'roll_call', label: 'Roll call' },
      gym.mobile_app_enabled && { value: 'mobile_app', label: 'Mobile app (proximity check in)' },
      {
        value: 'internal_calendar',
        label: 'Internal calendar / contact profile page',
      },
      gym.book_and_pay_enabled && {
        value: 'book_and_pay',
        label: 'Book & Pay purchases',
        note: (
          <div>
            (Note: applying this restriction in Book & Pay will prevent{' '}
            <strong>new contacts</strong> from making Book & Pay purchases.)
          </div>
        ),
      },
    ]);
  }, [gym.book_and_pay_enabled, gym.mobile_app_enabled]);

  const viewSampleMessage = (type) => {
    $modal.open({
      template: require('templates/gyms/gym_profile/view_sample_booking_notification_message_modal.html.slim'),
      resolve: {
        type: () => type,
        website: () => {
          const url = sources.website(gym.id);
          return api.get(url);
        },
      },
      controller: 'ViewSampleBookingNotificationMessageCtrl',
    });
  };

  const validate = () => {
    const errors: any = {};
    if (
      bookingDetail.required_waiver_attendance_policy === 'waiver_required_for_marking_attendance'
    ) {
      if (!bookingDetail.required_waiver_ids.length) {
        errors.required_waiver_ids = 'This field is required';
      }
      if (!bookingDetail.enforce_waiver_attendance_policy_in.length) {
        errors.enforce_waiver_attendance_policy_in = 'Please select at least one option';
      }
    }
    if (
      bookingDetail.instructor_event_reminder_policy === 'send_instructor_event_reminder_emails' &&
      bookingDetail.instructor_event_reminder_recipient_user_ids.length === 0
    ) {
      errors.instructor_event_reminder_recipient_user_ids = 'Please select at least one option';
    }
    setErrors(errors);
    return !_.isEmpty(errors);
  };

  const save = () => {
    const hasErrors = validate();

    if (hasErrors) {
      notification.error('Please fix the errors below');
      return;
    }

    loadingAnimation.show();

    const url = `gyms/${gym.id}/update_booking_detail`;

    api.put(url, { gym_booking_detail_attributes: bookingDetail }).then(
      (response) => {
        setBookingDetail(response);
        gym.gym_booking_detail = response;
        loadingAnimation.hide();
        notification.success('Saved successfully');
        setErrors({});
      },
      (response) => {
        notification.error('Save failed. Please fix the errors below');
        setErrors(response.data.errors);

        loadingAnimation.hide();
      },
    );
  };

  const saveAndApplyToAllEvents = (params, endpoint, change) => {
    const url = `gyms/${gym.id}/update_booking_detail`;

    api.put(url, { gym_booking_detail_attributes: params }).then(
      (response) => {
        setBookingDetail(response);
        gym.gym_booking_detail = response;
        setErrors({});
        ReactModal.open({
          component: 'BulkApplyDefaultEventSettingsModal',
          props: {
            endpoint,
            change,
          },
        });
      },
      (response) => {
        notification.error('Save failed. Please fix the errors below');
        setErrors(response.data.errors);

        loadingAnimation.hide();
      },
    );
  };

  const applyAllowedBookingsAfterDefault = () => {
    const params = {
      bookings_allowed_after_unit_default: bookingDetail.bookings_allowed_after_unit_default,
      bookings_allowed_after_number_default:
        bookingDetail.bookings_allowed_after_number_default || null,
    };

    const period =
      params.bookings_allowed_after_number_default &&
      plural(
        params.bookings_allowed_after_unit_default,
        params.bookings_allowed_after_number_default,
        true,
      );

    const change = params.bookings_allowed_after_number_default
      ? `Earliest booking can occur ${period} before class start time`
      : 'Earliest booking can occur <strong>at any time</strong>';

    saveAndApplyToAllEvents(params, 'apply_booking_detail_allow_bookings_after_default', change);
  };

  const applyAllowedBookingsBeforeDefault = () => {
    const params = {
      bookings_allowed_before_unit_default: bookingDetail.bookings_allowed_before_unit_default,
      bookings_allowed_before_number_default:
        bookingDetail.bookings_allowed_before_number_default || null,
    };

    const period =
      params.bookings_allowed_before_number_default &&
      plural(
        params.bookings_allowed_before_unit_default,
        params.bookings_allowed_before_number_default,
        true,
      );
    const change = params.bookings_allowed_before_number_default
      ? `Latest booking can occur ${period} before class start time`
      : 'Latest booking can occur <strong>at any time</strong>';

    saveAndApplyToAllEvents(params, 'apply_booking_detail_allow_bookings_before_default', change);
  };

  const applyCancelBeforeDefault = () => {
    const params = {
      cancel_before_unit_default: bookingDetail.cancel_before_unit_default,
      cancel_before_number_default: bookingDetail.cancel_before_number_default || null,
    };

    const period =
      params.cancel_before_number_default &&
      plural(params.cancel_before_unit_default, params.cancel_before_number_default, true);
    const change = params.cancel_before_number_default
      ? `Bookings can be cancelled up to ${period} before class start time`
      : 'Bookings can be cancelled <strong>at any time</strong>';

    saveAndApplyToAllEvents(params, 'apply_booking_detail_cancel_before_default', change);
  };

  useEffect(() => {
    if (
      bookingDetail.instructor_event_reminder_policy === 'send_instructor_event_reminder_emails'
    ) {
      extendBookingDetail({
        instructor_event_reminder_delivery_hour:
          bookingDetail.instructor_event_reminder_delivery_hour || 14,
        instructor_event_reminder_lead_days: bookingDetail.instructor_event_reminder_lead_days || 1,
      });
    }
  }, [bookingDetail.instructor_event_reminder_policy]);

  return (
    <div className="row margin-bottom">
      <div className="col-md-8">
        <h3>Messages</h3>
        <div className="form-group">
          <Label>Send booking confirmation emails to:</Label>
          <div className="row">
            <div className="col-md-6">
              <Select
                value={bookingDetail.send_booking_confirmation_email_to}
                options={[
                  {
                    label: 'Members only',
                    value: 'send_booking_confirmation_email_to_members',
                  },
                  {
                    label: 'Prospects only',
                    value: 'send_booking_confirmation_email_to_prospects',
                  },
                  {
                    label: 'All contacts',
                    value: 'send_booking_confirmation_email_to_all',
                  },
                  {
                    label: 'No contacts',
                    value: 'send_booking_confirmation_email_to_none',
                  },
                ]}
                onChange={(send_booking_confirmation_email_to) => {
                  extendBookingDetail({ send_booking_confirmation_email_to });
                }}
              />
            </div>
            <div className="col-md-6 margin-top-8">
              <Button variant="link" onClick={() => viewSampleMessage('confirmation_email')}>
                View sample email
              </Button>
            </div>
          </div>

          {sequencesAvailable &&
            bookingDetail.send_booking_confirmation_email_to !==
              'send_booking_confirmation_email_to_none' && (
              <strong>
                Note: {FeatureNames.SEQUENCE_PLURAL} functionality is enabled on your account. Please ensure your {FeatureNames.SEQUENCE_PLURAL_LOWERCASE} do not
                duplicate this notification.
              </strong>
            )}
          <div className="helper-text">
            Sent when a single booking is created for a future-dated event.
          </div>
        </div>
        <div className="form-group">
          <Checkbox
            checked={bookingDetail.send_trainer_booking_confirmation_email}
            onChange={(send_trainer_booking_confirmation_email) => {
              extendBookingDetail({ send_trainer_booking_confirmation_email });
            }}
          >
            Send booking confirmation email to instructors?
          </Checkbox>
        </div>
        <div className="form-group">
          <Label>Send booking reminder message to:</Label>
          <div className="row">
            <div className="col-md-6">
              <Select
                value={bookingDetail.send_booking_reminder_to}
                options={[
                  {
                    label: 'All contacts',
                    value: 'send_booking_reminder_to_both',
                  },

                  {
                    label: 'Members only',
                    value: 'send_booking_reminder_to_members',
                  },

                  {
                    label: 'Prospects only',
                    value: 'send_booking_reminder_to_prospects',
                  },

                  {
                    label: 'None',
                    value: 'send_booking_reminder_to_none',
                  },
                ]}
                onChange={(send_booking_reminder_to: string) => {
                  const send_member_booking_reminder_via =
                    ['send_booking_reminder_to_both', 'send_booking_reminder_to_members'].indexOf(
                      send_booking_reminder_to,
                    ) >= 0 &&
                    bookingDetail.send_member_booking_reminder_via ===
                      'send_member_booking_reminder_via_none'
                      ? 'send_member_booking_reminder_via_email'
                      : bookingDetail.send_member_booking_reminder_via;

                  const send_prospect_booking_reminder_via =
                    ['send_booking_reminder_to_both', 'send_booking_reminder_to_prospects'].indexOf(
                      send_booking_reminder_to,
                    ) >= 0 &&
                    bookingDetail.send_prospect_booking_reminder_via ===
                      'send_prospect_booking_reminder_via_none'
                      ? 'send_prospect_booking_reminder_via_email'
                      : bookingDetail.send_prospect_booking_reminder_via;

                  extendBookingDetail({
                    send_booking_reminder_to,
                    send_member_booking_reminder_via,
                    send_prospect_booking_reminder_via,
                  });
                }}
              />
            </div>

            <div className="col-md-6 margin-top-8">
              <Button variant="link" onClick={() => viewSampleMessage('reminder_email')}>
                View sample email
              </Button>
              <span className="margin-left-14">
                <Button variant="link" onClick={() => viewSampleMessage('reminder_sms')}>
                  View sample SMS
                </Button>
              </span>
            </div>
          </div>

          {sequencesAvailable &&
            bookingDetail.send_booking_reminder_to !== 'send_booking_reminder_to_none' && (
              <strong>
                Note: {FeatureNames.SEQUENCE_PLURAL} functionality is enabled on your account. Please ensure your {FeatureNames.SEQUENCE_PLURAL_LOWERCASE} do not
                duplicate this notification.
              </strong>
            )}

          <div className="helper-text">
            Reminders are sent when the event start time is 24 hours away. If the event is sooner
            than 1 day from now, reminders will be sent within the hour, provided the event is at
            least 3 hours away.
          </div>
        </div>

        {['send_booking_reminder_to_both', 'send_booking_reminder_to_members'].indexOf(
          bookingDetail.send_booking_reminder_to,
        ) >= 0 && (
          <div className="form-group">
            <Label>Send member booking reminders via: </Label>
            <Select
              value={bookingDetail.send_member_booking_reminder_via}
              options={_.compact([
                {
                  label: 'Email',
                  value: 'send_member_booking_reminder_via_email',
                },
                {
                  label: 'SMS',
                  value: 'send_member_booking_reminder_via_sms',
                },
                {
                  label: 'Email & SMS',
                  value: 'send_member_booking_reminder_via_all',
                },
                bookingDetail.send_member_booking_reminder_via ===
                  'send_member_booking_reminder_via_none' && {
                  label: 'None',
                  value: 'send_member_booking_reminder_via_none',
                },
              ])}
              onChange={(send_member_booking_reminder_via) => {
                extendBookingDetail({ send_member_booking_reminder_via });
              }}
            />
            {[
              'send_member_booking_reminder_via_sms',
              'send_member_booking_reminder_via_all',
            ].indexOf(bookingDetail.send_member_booking_reminder_via) >= 0 && <SmsUsageMessage />}
          </div>
        )}

        {['send_booking_reminder_to_both', 'send_booking_reminder_to_prospects'].indexOf(
          bookingDetail.send_booking_reminder_to,
        ) >= 0 && (
          <div className="form-group">
            <Label>Send prospect booking reminders via:</Label>
            <Select
              value={bookingDetail.send_prospect_booking_reminder_via}
              options={_.compact([
                {
                  label: 'Email',
                  value: 'send_prospect_booking_reminder_via_email',
                },
                {
                  label: 'SMS',
                  value: 'send_prospect_booking_reminder_via_sms',
                },
                {
                  label: 'Email & SMS',
                  value: 'send_prospect_booking_reminder_via_all',
                },
                bookingDetail.send_prospect_booking_reminder_via ===
                  'send_prospect_booking_reminder_via_none' && {
                  label: 'None',
                  value: 'send_prospect_booking_reminder_via_none',
                },
              ])}
              onChange={(send_prospect_booking_reminder_via) => {
                extendBookingDetail({ send_prospect_booking_reminder_via });
              }}
            />

            {[
              'send_prospect_booking_reminder_via_sms',
              'send_prospect_booking_reminder_via_all',
            ].indexOf(bookingDetail.send_prospect_booking_reminder_via) >= 0 && <SmsUsageMessage />}
          </div>
        )}
        <div className="form-group">
          <Checkbox
            checked={
              bookingDetail.instructor_event_reminder_policy ===
              'send_instructor_event_reminder_emails'
            }
            onChange={(sendDigest) => {
              extendBookingDetail({
                instructor_event_reminder_policy: sendDigest
                  ? 'send_instructor_event_reminder_emails'
                  : 'do_not_send_instructor_event_reminders',
              });
            }}
          >
            Send reminder digest emails listing upcoming events to instructors?
          </Checkbox>
          <div className="helper-text">
            Reminder digests are sent daily, giving each instructor a personalized list of their
            upcoming events for the day
          </div>
        </div>
        {bookingDetail.instructor_event_reminder_policy ===
          'send_instructor_event_reminder_emails' && (
          <>
            <div className="form-group">
              <Label>Send reminders to:</Label>
              <MultipleSelect
                fullWidth
                value={bookingDetail.instructor_event_reminder_recipient_user_ids}
                onChange={(instructor_event_reminder_recipient_user_ids) => {
                  extendBookingDetail({ instructor_event_reminder_recipient_user_ids });
                }}
                options={instructors.map((instructor) => {
                  return {
                    value: instructor.id,
                    label: instructor.name,
                  };
                })}
                placeholder="Select Instructors"
                hideEmptyOption
              />

              <ErrorMessage error={errors.instructor_event_reminder_recipient_user_ids} />
              <div className="helper-text">
                Each recipient will receive a personalized digest, containing only the events they
                are instructing.
              </div>

              <div className="helper-text">
                If an instructor has no upcoming events for the day, no email will be sent.
              </div>
            </div>

            <div className="row">
              <div className="form-group col-md-6">
                <Label>Send reminders daily at:</Label>
                <Select
                  options={hours}
                  value={bookingDetail.instructor_event_reminder_delivery_hour || 14}
                  onChange={(instructor_event_reminder_delivery_hour) =>
                    extendBookingDetail({
                      instructor_event_reminder_delivery_hour: Number(
                        instructor_event_reminder_delivery_hour,
                      ),
                    })
                  }
                />
              </div>
              <div className="form-group col-md-6">
                <Label>
                  For events scheduled {bookingDetail.instructor_event_reminder_lead_days} day(s) in
                  advance
                </Label>
                <Select
                  options={eventReminderLeadDaysOptions}
                  value={bookingDetail.instructor_event_reminder_lead_days || 1}
                  onChange={(instructor_event_reminder_lead_days) =>
                    extendBookingDetail({
                      instructor_event_reminder_lead_days: Number(
                        instructor_event_reminder_lead_days,
                      ),
                    })
                  }
                />
              </div>
            </div>
          </>
        )}
        <div className="form-group">
          <Label>Send waitlist booking confirmation message via:</Label>
          <Select
            value={bookingDetail.send_waitlist_booking_confirmation_via}
            options={[
              {
                label: 'Email',
                value: 'send_waitlist_booking_confirmation_via_email',
              },
              {
                label: 'SMS',
                value: 'send_waitlist_booking_confirmation_via_sms',
              },
              {
                label: 'Email & SMS',
                value: 'send_waitlist_booking_confirmation_via_all',
              },
              {
                label: 'None',
                value: 'send_waitlist_booking_confirmation_via_none',
              },
            ]}
            onChange={(send_waitlist_booking_confirmation_via) => {
              extendBookingDetail({ send_waitlist_booking_confirmation_via });
            }}
          />

          {[
            'send_waitlist_booking_confirmation_via_sms',
            'send_waitlist_booking_confirmation_via_all',
          ].indexOf(bookingDetail.send_waitlist_booking_confirmation_via) >= 0 && (
            <SmsUsageMessage />
          )}

          <div className="helper-text">
            Sent when booking is created for a contact on an event waitlist.
          </div>
        </div>

        <div className="margin-top">
          <h3>Calendar</h3>

          <div className="form-group">
            <Label>Week starts on:</Label>
            <Select
              value={bookingDetail.calendar_start_wday}
              onChange={(calendar_start_wday) => {
                extendBookingDetail({ calendar_start_wday });
              }}
              options={[
                { value: 0, label: 'Sunday' },
                { value: 1, label: 'Monday' },
              ]}
            />
            <CalendarLink />
          </div>
        </div>

        {gym.advanced_booking_settings_enabled && (
          <div className="margin-top">
            <h3>Event Defaults</h3>
            <p>The following settings will be applied by default when creating new event series:</p>
            <div className="form-group">
              <div className="flex">
                <Switch
                  checked={bookingDetail.restrict_bookings_after}
                  onChange={(restrict_bookings_after) => {
                    const data = restrict_bookings_after
                      ? { bookings_allowed_after_unit_default: bookingsAllowedAfterUnits[0][0] }
                      : {
                          bookings_allowed_after_number_default: null,
                          bookings_allowed_after_unit_default: null,
                        };

                    extendBookingDetail({ restrict_bookings_after, ...data });
                  }}
                />
                <div className="margin-left">
                  Earliest booking for this event can occur
                  <fieldset
                    className="inline-flex margin-left-8"
                    disabled={!bookingDetail.restrict_bookings_after}
                  >
                    <Input
                      type="number"
                      className="form-control--xs"
                      size="xs"
                      min={0}
                      name="bookings_allowed_after_number_default"
                      value={bookingDetail.bookings_allowed_after_number_default}
                      onChange={(bookings_allowed_after_number_default) => {
                        extendBookingDetail({ bookings_allowed_after_number_default });
                      }}
                    />
                    <div className="margin-left-8">
                      <Select
                        value={bookingDetail.bookings_allowed_after_unit_default}
                        onChange={(bookings_allowed_after_unit_default) => {
                          extendBookingDetail({ bookings_allowed_after_unit_default });
                        }}
                        options={bookingsAllowedAfterUnits}
                        getValue={(item) => item[0]}
                        getLabel={(item) => item[1]}
                        size="sm"
                      ></Select>
                    </div>
                  </fieldset>
                  <span className="margin-left-8">
                    before class start time
                    <br />
                    (applies to bookings made from mobile app / website calendar)
                  </span>
                  <div>
                    <Button
                      size="sm"
                      onClick={() => applyAllowedBookingsAfterDefault()}
                      variant="link"
                    >
                      Apply this setting to all existing events
                    </Button>
                  </div>
                </div>
              </div>
              <ErrorMessage error={errors.bookings_allowed_after_number_default} />
            </div>
            <div className="form-group">
              <div className="flex">
                <Switch
                  checked={bookingDetail.restrict_bookings_before}
                  onChange={(restrict_bookings_before) => {
                    const data = restrict_bookings_before
                      ? { bookings_allowed_before_unit_default: restrictCancellationUnits[0][0] }
                      : {
                          bookings_allowed_before_number_default: null,
                          bookings_allowed_before_unit_default: null,
                        };

                    extendBookingDetail({ restrict_bookings_before, ...data });
                  }}
                />
                <div className="margin-left">
                  Latest booking can occur
                  <fieldset
                    className="inline-flex margin-left-8"
                    disabled={!bookingDetail.restrict_bookings_before}
                  >
                    <Input
                      type="number"
                      className="form-control--xs"
                      size="xs"
                      min={0}
                      name="bookings_allowed_before_number_default"
                      value={bookingDetail.bookings_allowed_before_number_default}
                      onChange={(bookings_allowed_before_number_default) => {
                        extendBookingDetail({ bookings_allowed_before_number_default });
                      }}
                    />
                    <div className="margin-left-8">
                      <Select
                        value={bookingDetail.bookings_allowed_before_unit_default}
                        onChange={(bookings_allowed_before_unit_default) => {
                          extendBookingDetail({ bookings_allowed_before_unit_default });
                        }}
                        options={restrictCancellationUnits}
                        getValue={(item) => item[0]}
                        getLabel={(item) => item[1]}
                        size="sm"
                      ></Select>
                    </div>
                  </fieldset>
                  <span className="margin-left-8">before class start time</span>
                  <div>
                    <Button
                      size="sm"
                      onClick={() => applyAllowedBookingsBeforeDefault()}
                      variant="link"
                    >
                      Apply this setting to all existing events
                    </Button>
                  </div>
                </div>
              </div>
              <ErrorMessage error={errors.bookings_allowed_before_number_default} />
            </div>
            <div className="form-group">
              <div className="flex">
                <Switch
                  checked={bookingDetail.restrict_cancellations}
                  onChange={(restrict_cancellations) => {
                    const data = restrict_cancellations
                      ? { cancel_before_unit_default: restrictCancellationUnits[0][0] }
                      : { cancel_before_number_default: null, cancel_before_unit_default: null };

                    extendBookingDetail({ restrict_cancellations, ...data });
                  }}
                />
                <div className="margin-left">
                  Bookings can be cancelled up to
                  <fieldset
                    className="inline-flex margin-left-8"
                    disabled={!bookingDetail.restrict_cancellations}
                  >
                    <Input
                      type="number"
                      className="form-control--xs"
                      size="xs"
                      min={0}
                      name="cancel_before_number_default"
                      value={bookingDetail.cancel_before_number_default}
                      onChange={(cancel_before_number_default) => {
                        extendBookingDetail({ cancel_before_number_default });
                      }}
                    />
                    <div className="margin-left-8">
                      <Select
                        value={bookingDetail.cancel_before_unit_default}
                        onChange={(cancel_before_unit_default) => {
                          extendBookingDetail({ cancel_before_unit_default });
                        }}
                        options={restrictCancellationUnits}
                        getValue={(item) => item[0]}
                        getLabel={(item) => item[1]}
                        size="sm"
                      ></Select>
                    </div>
                  </fieldset>
                  <span className="margin-left-8">before class start time</span>
                  <div>
                    <Button size="sm" onClick={() => applyCancelBeforeDefault()} variant="link">
                      Apply this setting to all existing events
                    </Button>
                  </div>
                </div>
              </div>
              <ErrorMessage error={errors.cancel_before_number_default} />
            </div>
          </div>
        )}

        <div className="margin-top">
          <h3>Advanced</h3>
          <div className="form-group">
            <Label>Maximum bookings allowed for each Prospect (when booking from Website):</Label>
            <div>
              <Input
                value={bookingDetail.prospect_booking_limit}
                type="number"
                size="sm"
                onChange={(prospect_booking_limit) => {
                  extendBookingDetail({ prospect_booking_limit });
                }}
              />
            </div>
            <ErrorMessage error={errors.prospect_booking_limit} />
            <div className="helper-text">
              Leave blank for no limit; 0 = no bookings allowed for Prospects.
            </div>
          </div>

          <div className="form-group">
            <Checkbox
              checked={bookingDetail.enforce_event_prospect_attendance_limit_for_free_classes}
              onChange={(enforce_event_prospect_attendance_limit_for_free_classes) => {
                extendBookingDetail({ enforce_event_prospect_attendance_limit_for_free_classes });
              }}
            >
              Enforce event prospect attendee limit for 'free' classes?
            </Checkbox>

            <div className="helper-text">
              Select this option if you want to limit the number of prospects that can book into
              free classes/events.
            </div>
          </div>

          <div className="form-group">
            <Checkbox
              checked={bookingDetail.exclude_inactive_members_from_free_classes}
              onChange={(exclude_inactive_members_from_free_classes) => {
                extendBookingDetail({ exclude_inactive_members_from_free_classes });
              }}
            >
              Exclude cancelled members from 'free' classes?
            </Checkbox>
            <div className="helper-text">
              Select this option if you do not wish to allow cancelled members to book into free
              classes/events.
            </div>
          </div>

          <div className="form-group">
            <Checkbox
              checked={bookingDetail.waitlist_bookings_are_free}
              onChange={(waitlist_bookings_are_free) => {
                extendBookingDetail({ waitlist_bookings_are_free });
              }}
            >
              Ignore membership requirements when making bookings from waitlists (Active Members only)?
            </Checkbox>
            <div className="helper-text">
              While this setting is enabled, any bookings made from waitlists for active members
              will be free classes.
            </div>
          </div>

          <div className="form-group">
            <Checkbox
              checked={
                bookingDetail.required_waiver_attendance_policy ===
                'waiver_required_for_marking_attendance'
              }
              onChange={(required) => {
                const enforce_waiver_attendance_policy_in = required
                  ? ['roll_call', 'class_kiosk', 'general_kiosk']
                  : [];

                extendBookingDetail({
                  required_waiver_attendance_policy: required
                    ? 'waiver_required_for_marking_attendance'
                    : 'waiver_not_required',
                  enforce_waiver_attendance_policy_in,
                });
              }}
            >
              Require waiver(s) to be signed before contact can attend classes?
            </Checkbox>

            {bookingDetail.required_waiver_attendance_policy ===
              'waiver_required_for_marking_attendance' && (
              <div className="margin-top-12">
                <div className="margin-bottom-8">
                  <div className="inline-flex align-center flex-wrap">
                    <div className="margin-right-8">
                      Before attending classes, contact must sign
                    </div>
                    {bookingDetail.required_waiver_ids?.length > 1 && (
                      <Select
                        className="margin-right-8"
                        value={bookingDetail.require_waivers_with}
                        onChange={(require_waivers_with) => {
                          extendBookingDetail({ require_waivers_with });
                        }}
                        options={[
                          { value: 'require_all_waivers', label: 'All' },
                          { value: 'require_any_waiver', label: 'Any' },
                        ]}
                        size="sm"
                        variant="v2"
                        width="auto"
                      />
                    )}
                    <div className="inline-flex align-center">
                      {bookingDetail.required_waiver_ids?.length > 1 && (
                        <div className="margin-right-8">of the waiver(s)</div>
                      )}
                      <MultipleSelect
                        value={bookingDetail.required_waiver_ids}
                        onChange={(required_waiver_ids) => {
                          extendBookingDetail({ required_waiver_ids });
                        }}
                        options={waivers}
                        size="sm"
                        placeholder="Select"
                        hideEmptyOption
                      />
                    </div>
                  </div>
                  <ErrorMessage error={errors.required_waiver_ids} />
                </div>

                <div className="margin-bottom-8">
                  Apply this restriction in the following places:
                </div>
                <div className="margin-left-24 margin-bottom-12">
                  {waiverAttendancePolicyOptions.map((option) => (
                    <div className="margin-bottom-8" key={option.value}>
                      <Checkbox
                        checked={
                          bookingDetail.enforce_waiver_attendance_policy_in?.indexOf(
                            option.value,
                          ) >= 0
                        }
                        onChange={(checked) => {
                          const current = bookingDetail.enforce_waiver_attendance_policy_in || [];
                          const updated = checked
                            ? [...current, option.value]
                            : _.without(current, option.value);
                          extendBookingDetail({
                            enforce_waiver_attendance_policy_in: updated,
                          });
                        }}
                      >
                        {option.label}
                      </Checkbox>
                      {option.note && <div className="margin-left-24">{option.note}</div>}
                    </div>
                  ))}

                  <ErrorMessage error={errors.enforce_waiver_attendance_policy_in} />
                </div>

                <div>
                  <Label>
                    (Optional) Customize the message shown to contacts when attendance is blocked
                    due to an unsigned waiver:
                  </Label>

                  <Textarea
                    value={bookingDetail.required_waiver_contact_failure_message_override}
                    onChange={(required_waiver_contact_failure_message_override) =>
                      extendBookingDetail({ required_waiver_contact_failure_message_override })
                    }
                    placeholder="Sorry! You're currently unable to perform this action until the required forms have been signed. Please contact your instructor for more information."
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <Label>Mark bookings absent automatically, if attendance has not been set?</Label>
            <Select
              value={bookingDetail.mark_bookings_absent_automatically.toString()}
              options={[
                {
                  label: 'Yes',
                  value: 'true',
                },
                {
                  label: 'No',
                  value: 'false',
                },
              ]}
              onChange={(mark_bookings_absent_automatically: string) => {
                const val = JSON.parse(mark_bookings_absent_automatically);

                const mark_bookings_absent_based_on = val
                  ? 'mark_bookings_absent_based_on_event_start'
                  : null;

                extendBookingDetail({
                  mark_bookings_absent_automatically: val,
                  mark_bookings_absent_based_on,
                });
              }}
            />
          </div>

          {bookingDetail.mark_bookings_absent_automatically && (
            <div className="form-group inline-compact">
              <div className="inline-module">Automatically mark bookings absent</div>
              <div className="inline-module vertical-align-top">
                <Input
                  value={bookingDetail.mark_bookings_absent_after_hours}
                  type="number"
                  size="xs"
                  onChange={(mark_bookings_absent_after_hours) => {
                    extendBookingDetail({ mark_bookings_absent_after_hours });
                  }}
                />
                <ErrorMessage error={errors.mark_bookings_absent_after_hours} />
              </div>
              <div className="inline-module">hours after</div>
              <div className="inline-module">
                <Select
                  size="sm"
                  value={bookingDetail.mark_bookings_absent_based_on}
                  options={[
                    {
                      label: 'Event starts',
                      value: 'mark_bookings_absent_based_on_event_start',
                    },
                    {
                      label: 'Event ends',
                      value: 'mark_bookings_absent_based_on_event_end',
                    },
                  ]}
                  onChange={(mark_bookings_absent_based_on) => {
                    extendBookingDetail({ mark_bookings_absent_based_on });
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <Button variant="success" onClick={save}>
          Save
        </Button>
      </div>
    </div>
  );
};

angular.module('gym').value('GymProfileBookings', GymProfileBookings);
